import React from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Button as MuiButton,
  CardActions,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import HomeIcon from "@mui/icons-material/Home";
import { Formik } from "formik";
import { validationSchema } from "./utils";
import {
  FieldAPIKey,
  FieldAppID,
  FieldConfigVersions,
  FieldDatabaseUrl,
  FieldDeeplinkOpenApp,
  FieldFirebaseServiceAccountJSON,
  FieldGameServers,
  FieldHost,
  FieldIsSecure,
  FieldName,
  FieldPort,
  FieldStorageBucket,
} from "./components/Fields";

const Card = styled(MuiCard)`
  ${spacing};

  box-shadow: none;
`;

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

function GamesNew() {
  const navigate = useNavigate();

  return (
    <React.Fragment>
      <Helmet title="Create New Game" />

      <Typography variant="h3" gutterBottom display="inline">
        Create New Game
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/games">
          Games
        </Link>
        <Typography>Create New Game</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Formik
        initialValues={{
          app_id: "",
          firebase_service_account_json: "",
          api_key: "",
          database_url: "",
          game_servers: "",
          deeplink_open_app: "",
          name: "",
          is_secure: false,
          host: "",
          port: "",
          storage_bucket: "",
          config_versions: [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (
          {
            firebase_service_account_json,
            game_servers,
            config_versions,
            ...values
          },
          { setErrors, setStatus, setSubmitting }
        ) => {
          try {
            const { data } = await axios.post(`/api/v1/games`, {
              firebase_service_account_json: JSON.parse(
                firebase_service_account_json
              ),
              game_servers: JSON.parse(game_servers),
              config_versions: config_versions.reduce(
                (configVersions, version) => ({
                  ...configVersions,
                  [version.key]: version.value,
                }),
                {}
              ),
              ...values,
            });

            navigate(`/games/${data.id}`);
          } catch (error) {
            console.log(error);
            const message =
              (error.messages && error.messages.join("\n")) ||
              "Something went wrong";

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleSubmit, isSubmitting, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Grid container spacing={4}>
                  {errors.submit && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{errors.submit}</Alert>
                    </Grid>
                  )}
                  <Grid item md={6} xs={12}>
                    <FieldAppID />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldName />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldAPIKey />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldDeeplinkOpenApp />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldStorageBucket />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldDatabaseUrl />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldHost />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldPort />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldFirebaseServiceAccountJSON />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldGameServers />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldIsSecure />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FieldConfigVersions value={values.config_versions} />
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Create Game
                </Button>
                <Button color="primary" onClick={() => navigate("/games")}>
                  Cancel
                </Button>
              </CardActions>
            </Card>
          </form>
        )}
      </Formik>
    </React.Fragment>
  );
}

export default GamesNew;
